import { useContext, useState } from "react";
import ToastContext from "../../../context/toastProvider";
import { createUserAddress } from "../functions";
import Button from "../../atoms/button";
import Loading from "../../atoms/loading";
import { isNumeric } from "../../utils/functions";
import { Field, inputClasses } from "../../atoms/formField";

/** Máscara visual 00000-000 (o valor guardado segue só dígitos). */
const formatCep = (value = "") =>
    value.length > 5 ? `${value.slice(0, 5)}-${value.slice(5)}` : value;

const NewAddress = ({ user, cancel, userId, refresh }) => {

    const { goodToast, badToast } = useContext(ToastContext);

    const [loading, setLoading] = useState(false);
    const [noCEP, setNoCEP] = useState(false);
    const [cepFound, setCepFound] = useState(false);

    const [name, setName] = useState("");
    const [cep, setCep] = useState("");
    const [street, setStreet] = useState("");
    const [district, setDistrict] = useState("");
    const [houseNumber, setHouseNumber] = useState("");
    const [city, setCity] = useState("");
    const [state, setState] = useState("");
    const [reference, setReference] = useState("");
    const [complement, setComplement] = useState("");

    const handleSubmit = async () => {
        if (disableButton) {
            return null;
        }
        setLoading(true);
        const body = {
            userId,
            receiver: name,
            name,
            postalCode: cep,
            street,
            district,
            houseNumber,
            city,
            state,
            complement,
            reference
        }
        const data = await createUserAddress(user?.token, body);
        if (data?.type !== "success") {
            badToast(data?.message ?? "Erro ao criar endereço");
            setLoading(false);
            return null;
        }
        refresh();
        setLoading(false);
        goodToast("Endereço criado!");
    }


    const searchCEP = async (postal) => {
        try {
            setLoading(true);
            setCepFound(false);
            const response = await fetch(`https://viacep.com.br/ws/${postal}/json/`);
            if (response.ok) {
                setNoCEP(false);
                const data = await response.json();
                if (data.erro) {
                    setNoCEP(true);
                    return null;
                }
                setStreet(data.logradouro);
                setDistrict(data.bairro)
                setCity(data.localidade)
                setState(data.uf)
                setCepFound(true);
            } else {
                setNoCEP(true);
            }
        } catch (error) {
            setNoCEP(true);
        } finally {
            setLoading(false);
        }
    }

    const handleCep = (value) => {
        const tempValue = value?.split("-")?.join("");
        if (tempValue === "" || isNumeric(tempValue)) {
            setCep(tempValue);
            setNoCEP(false);
            if (tempValue.length === 8) {
                searchCEP(tempValue);
            }
        }
    }

    const disableButton = !cep || !name || !street || !state || !district || !houseNumber;

    return (
        <div className="relative mx-auto max-w-xl text-left">
            <h2 className="mb-1 text-lg font-semibold text-laranjinha">Novo endereço</h2>
            <p className="mb-4 text-sm text-laranjinha/60">
                Informe o CEP para preencher o endereço automaticamente. Campos com <span className="text-primary">*</span> são obrigatórios.
            </p>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Nome do recebedor" required className="sm:col-span-2">
                    <input type="text" className={inputClasses} placeholder="Quem vai receber o pacote" value={name} onChange={e => setName(e.target.value)} />
                </Field>

                <Field label="CEP" required>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            inputMode="numeric"
                            className={`${inputClasses} w-32`}
                            placeholder="00000-000"
                            maxLength={9}
                            value={formatCep(cep)}
                            onChange={e => handleCep(e.target.value)}
                        />
                        <button
                            type="button"
                            id="cep"
                            onClick={() => searchCEP(cep)}
                            disabled={loading || cep.length !== 8}
                            className="whitespace-nowrap rounded-lg border border-primary/40 px-3 py-1 text-sm text-laranjinha hover:border-primary disabled:cursor-not-allowed disabled:opacity-40"
                        >
                            ⌕ Buscar CEP
                        </button>
                    </div>
                    {noCEP ? <span className="text-xs text-red-500">CEP não encontrado — preencha o endereço manualmente.</span> : null}
                    {cepFound ? <span className="text-xs text-green-500">Endereço preenchido pelo CEP — confira e complete.</span> : null}
                </Field>

                <Field label="Número" required>
                    <input type="text" className={inputClasses} value={houseNumber} onChange={e => setHouseNumber(e.target.value)} />
                </Field>

                <Field label="Logradouro" required className="sm:col-span-2">
                    <input type="text" className={inputClasses} placeholder="Rua, avenida…" value={street} onChange={e => setStreet(e.target.value)} />
                </Field>

                <Field label="Bairro" required>
                    <input type="text" className={inputClasses} value={district} onChange={e => setDistrict(e.target.value)} />
                </Field>

                <Field label="Complemento">
                    <input type="text" className={inputClasses} placeholder="Apto, bloco… (opcional)" value={complement} onChange={e => setComplement(e.target.value)} />
                </Field>

                <Field label="Cidade">
                    <input type="text" className={inputClasses} value={city} onChange={e => setCity(e.target.value)} />
                </Field>

                <Field label="Estado" required>
                    <input type="text" className={inputClasses} maxLength={2} placeholder="UF" value={state} onChange={e => setState(e.target.value)} />
                </Field>

                <Field label="Ponto de referência" className="sm:col-span-2">
                    <input type="text" className={inputClasses} placeholder="Ex.: próximo ao mercado (opcional)" value={reference} onChange={e => setReference(e.target.value)} />
                </Field>
            </div>

            <div className="m-auto mt-5 flex w-fit gap-3">
                <Button classes="bg-red-400 hover:bg-red-300 px-4" click={cancel}>← Voltar</Button>
                <Button classes="bg-green-400 hover:bg-green-300 px-6" disabled={disableButton} click={handleSubmit}>Salvar endereço</Button>
            </div>
            {/* LOADING OVERLAY */}
            {
                loading ?
                    <div className="absolute top-0 left-0 w-full h-full bg-[#000000dd] flex justify-center items-center">
                        <Loading />
                    </div>
                    : null
            }
        </div>
    )
}

export default NewAddress;
